import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { EmptyState, PrimaryButton, StatusPill } from '@/lib';
import { setStoredToken, setStoredOrgSlug } from '@/app/auth';
import { useAcceptInvitation, useInvitationByToken } from './queries';

/**
 * SInviteAccept — landing page for the /invite?token=... link emailed
 * to new users. Renders a preview of the invitation, asks for the
 * display name, then POSTs the accept and routes to /. SSO branch is
 * deferred to PR-19 (OIDC).
 */
const MIN_PASSWORD_LEN = 8;

export function SInviteAccept() {
  const search = useSearch({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const token = search.token ?? '';
  const invite = useInvitationByToken(token);
  const accept = useAcceptInvitation(token);
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  if (!token) {
    return (
      <EmptyState
        variant="error"
        title="Missing invitation token"
        body="Open the link from your invitation email."
      />
    );
  }

  if (invite.isLoading) {
    return (
      <p className="text-fs-body text-gray-700">Loading invitation…</p>
    );
  }

  if (invite.isError || !invite.data) {
    return (
      <EmptyState
        variant="error"
        title="Invitation not found"
        body="The link might be expired or already used. Ask your inviter to send a new one."
      />
    );
  }

  const inv = invite.data;
  const expired = inv.status === 'expired';
  const used = inv.status === 'accepted';

  if (expired || used) {
    return (
      <EmptyState
        variant="error"
        title={used ? 'Invitation already accepted' : 'Invitation expired'}
        body={
          used
            ? 'This invitation was already used. Sign in to continue, or ask for a new invite.'
            : 'Ask your inviter to send a new one.'
        }
      />
    );
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password.length < MIN_PASSWORD_LEN) {
      setLocalError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setLocalError("Passwords don't match.");
      return;
    }
    accept.mutate(
      { displayName: displayName.trim() || undefined, password },
      {
        onSuccess: (data: { token?: string; tenant_slug?: string; tenant_id?: string }) => {
          // Accept now returns a session JWT — drop the prospect
          // directly into their org instead of bouncing through /auth.
          // Pre-PR, accept returned only the user record so the FE had
          // to send them to /auth for a second login round-trip.
          if (data?.token) {
            setStoredToken(data.token);
            const slug = data.tenant_slug ?? data.tenant_id ?? '';
            if (slug) setStoredOrgSlug(slug);
            navigate({ to: slug ? `/${slug}/` : '/' });
            return;
          }
          // Fallback: backend ran without a configured minter (returns
          // accept response with no token field). Send the prospect to
          // /auth to sign in with the password they just chose.
          navigate({ to: '/auth' });
        },
      },
    );
  };

  return (
    <div className="mx-auto flex max-w-md flex-col gap-sp-6 pt-sp-9">
      <header className="flex flex-col gap-sp-2">
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          You've been invited
        </p>
        <h1 className="text-fs-h1 font-medium text-gray-900">
          Join {inv.tenant_id}
        </h1>
        <p className="text-fs-body text-gray-700">
          {inv.invited_by} invited you to join as{' '}
          <StatusPill level="info" label={inv.role} variant="outlined" />
        </p>
        {inv.group_ids && inv.group_ids.length > 0 ? (
          <p className="text-fs-meta text-gray-700">
            You'll be added to {inv.group_ids.length} group
            {inv.group_ids.length === 1 ? '' : 's'} on accept.
          </p>
        ) : null}
      </header>

      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-sp-4 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      >
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Email
          <input
            disabled
            value={inv.email}
            className="rounded-r-default border border-gray-300 bg-gray-100 px-sp-2 py-sp-1 text-gray-700"
          />
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Display name
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={inv.display_name || inv.email}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
          />
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Password
          <input
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LEN}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={`At least ${MIN_PASSWORD_LEN} characters`}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
          />
        </label>
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Confirm password
          <input
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LEN}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1"
          />
        </label>
        <div className="flex items-center gap-sp-3">
          <PrimaryButton
            type="submit"
            label={accept.isPending ? 'Joining…' : 'Accept invitation'}
            disabled={accept.isPending}
          />
          {localError ? (
            <span role="alert" className="text-fs-meta text-critical">
              {localError}
            </span>
          ) : accept.isError ? (
            <span role="alert" className="text-fs-meta text-critical">
              {accept.error instanceof Error ? accept.error.message : String(accept.error)}
            </span>
          ) : null}
        </div>
        <p className="text-fs-meta text-gray-700">
          By accepting, an account is created for you in {inv.tenant_id}.
          You'll be redirected to sign in.
        </p>
      </form>
    </div>
  );
}
